/**
 * RowFlex - A flexbox component for arranging children in a row.
 *
 * @author Koh Tong Seng <tongsengkoh@ifca.com.my>
 * @since 2023-07-12
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} [props.children] - The children to render
 * @param {string} [props.className] - The CSS class name to apply
 * @param {AlignItemsEnum} [props.crossAxisAlignment] - The cross axis alignment
 * @param {'row'} props.flexDirection - The flexbox direction
 * @param {boolean} [props.fullWidth] - Whether to take up the full width
 * @param {string} [props.gap] - The gap between children
 * @param {string} [props.height] - The height of the component
 * @param {JustifyContentEnum} [props.mainAxisAlignment] - The main axis alignment
 * @param {OverflowEnum} [props.overflow] - The overflow strategy
 *
 * @returns {JSX.Element} The rendered component
 */

import React from 'react'
import { FlexX } from '../FlexX'
import {
  AlignItemsEnum,
  JustifyContentEnum,
  OverflowEnum,
} from '../LayoutXenum'

type RowFlexProps = {
  children?: React.ReactNode
  className?: string
  crossAxisAlignment?: AlignItemsEnum
  fullWidth?: boolean
  gap?: string
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
}

export const RowFlex = React.memo<RowFlexProps>(
  ({
    children,
    className,
    crossAxisAlignment,
    fullWidth,
    gap,
    height,
    mainAxisAlignment,
    overflow,
  }: RowFlexProps) => (
    <FlexX
      className={className}
      crossAxisAlignment={crossAxisAlignment}
      flexDirection="row"
      fullWidth={fullWidth}
      gap={gap}
      height={height}
      mainAxisAlignment={mainAxisAlignment}
      overflow={overflow}
    >
      {children}
    </FlexX>
  )
)
